import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import milltechLogo from '../../assets/miltech logo.png';
import rocketIconImg from '../../assets/rocket-removebg-preview.png';
import './Navbar.css';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Machines', to: '/machines' },
  { label: 'Products', to: '/products' },
  {
    label: 'Services',
    to: '/services',
    dropdown: [
      { label: 'Aerospace & Defence', to: '/services/aerospace', icon: 'rocket-img', desc: 'ISRO · BrahMos · Gaganyaan' },
      { label: 'Windmill Components', to: '/services/windmill', icon: '🌬️', desc: 'Renewable energy parts' },
      { label: 'Cement Machinery', to: '/services/cement', icon: '⚙️', desc: 'Heavy industrial components' },
    ],
  },
  { label: 'Contact Us', to: '/contact' },
  { label: 'About Us', to: '/about' },
];

// Mobile-only links (Dashboard etc.)


/* Pages that have a full-bleed dark hero — navbar starts transparent on these */
const TRANSPARENT_ROUTES = ['/', '/services/aerospace', '/services/windmill', '/services/cement', '/services', '/about', '/contact', '/machines', '/products'];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTimer = useRef(null);
  const location = useLocation();

  const isTransparentPage = TRANSPARENT_ROUTES.some(r => location.pathname === r || location.pathname.startsWith(r + '/'));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
    setScrolled(window.scrollY > 60);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleMouseEnter = (label) => {
    clearTimeout(dropdownTimer.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimer.current = setTimeout(() => setActiveDropdown(null), 180);
  };

  const transparent = isTransparentPage && !scrolled;

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${transparent ? 'navbar--transparent' : ''}`}>
        <div className="container navbar__inner">
          {/* Logo */}
          <Link to="/" className="navbar__logo">
            {/* Logo image — transparent bg, no border/shadow when transparent */}
            <div className="navbar__logo-img-wrap">
              <img src={milltechLogo} alt="MILLTECH CNC" className="navbar__logo-img" />
            </div>
            <div className="navbar__logo-text">
              {/* MILLTECH CNC — red italic, slightly diagonal */}
              <span className="navbar__logo-brand">MILLTECH CNC</span>
              {/* Engineering Excellence Since 2002 — float animation, corrected position */}
              <span className="navbar__logo-tagline">
                <span className="navbar__logo-tagline-inner navbar__logo-float">
                  Engineering Excellence <em className="navbar__logo-year">Since 2002</em>
                </span>
              </span>
            </div>
          </Link>

          {/* Desktop links */}
          <ul className="navbar__links">
            {NAV_LINKS.map((link) => (
              <li
                key={link.label}
                className={`navbar__item ${link.dropdown ? 'navbar__item--dropdown' : ''}`}
                onMouseEnter={() => link.dropdown && handleMouseEnter(link.label)}
                onMouseLeave={() => link.dropdown && handleMouseLeave()}
              >
                <NavLink
                  to={link.to}
                  end={link.to === '/'}
                  className={({ isActive }) => `navbar__link ${isActive ? 'navbar__link--active' : ''}`}
                >
                  {link.label}
                  {link.dropdown && (
                    <svg className={`navbar__chevron ${activeDropdown === link.label ? 'open' : ''}`} viewBox="0 0 12 12" fill="none">
                      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </NavLink>

                {link.dropdown && (
                  <div className={`navbar__dropdown ${activeDropdown === link.label ? 'navbar__dropdown--open' : ''}`}>
                    <div className="navbar__dropdown-inner">
                      <p className="navbar__dropdown-label">Our Services</p>
                      {link.dropdown.map((item) => (
                        <Link key={item.label} to={item.to} className="navbar__dropdown-item">
                          <span className="navbar__dropdown-icon">
                            {item.icon === 'rocket-img'
                              ? <img src={rocketIconImg} alt="Aerospace" className="navbar__dropdown-icon-img" />
                              : item.icon}
                          </span>
                          <span>
                            <span className="navbar__dropdown-name">{item.label}</span>
                            <span className="navbar__dropdown-desc">{item.desc}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>

          <Link to="/contact" className="btn btn-primary navbar__cta">Get Quote</Link>

          <button
            className={`navbar__hamburger ${mobileOpen ? 'navbar__hamburger--open' : ''}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            <span/><span/><span/>
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)}/>}

      {/* Mobile drawer */}
      <div className={`mobile-drawer ${mobileOpen ? 'mobile-drawer--open' : ''}`}>
        <div className="mobile-drawer__header">
          <img src={milltechLogo} alt="MILLTECH CNC" className="mobile-drawer__logo" />
          <button className="mobile-drawer__close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="mobile-drawer__inner">
          <ul className="mobile-drawer__links">
            {NAV_LINKS.map((link) => (
              <li key={link.label} className="mobile-drawer__item">
                {link.dropdown ? (
                  <>
                    <button
                      className="mobile-drawer__link"
                      onClick={() => setMobileExpanded(mobileExpanded === link.label ? null : link.label)}
                    >
                      {link.label}
                      <svg className={`mobile-drawer__chevron ${mobileExpanded === link.label ? 'open' : ''}`} viewBox="0 0 12 12" fill="none">
                        <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    {mobileExpanded === link.label && (
                      <ul className="mobile-drawer__sub">
                        {link.dropdown.map((sub) => (
                          <li key={sub.label}>
                            <Link to={sub.to} className="mobile-drawer__sub-link">
                              {sub.icon === 'rocket-img'
                                ? <img src={rocketIconImg} alt="" className="mobile-drawer__sub-icon-img" />
                                : <span>{sub.icon}</span>}
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={link.to}
                    end={link.to === '/'}
                    className={({ isActive }) => `mobile-drawer__link ${isActive ? 'mobile-drawer__link--active' : ''}`}
                  >
                    {link.label}
                  </NavLink>
                )}
              </li>
            ))}
            {/* Dashboard — mobile only */}
            <li className="mobile-drawer__item mobile-drawer__item--dashboard">
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `mobile-drawer__link mobile-drawer__link--dashboard ${isActive ? 'mobile-drawer__link--active' : ''}`}
              >
                <span className="mobile-drawer__dash-icon">📊</span>
                Dashboard
              </NavLink>
            </li>
          </ul>

          <div className="mobile-drawer__footer">
            <Link to="/contact" className="btn btn-primary" style={{width:'100%',justifyContent:'center'}}>
              Get a Quote
            </Link>
            <div className="mobile-drawer__contact-links">
              <a href="tel:+919444058659" className="mobile-drawer__contact-link">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
                +91 94440 58659
              </a>
              <a href="tel:+918072515869" className="mobile-drawer__contact-link">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
                +91 80725 15869
              </a>
              <a href="mailto:btharan76@gmail.com" className="mobile-drawer__contact-link">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                btharan76@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard — fixed corner button on mobile only */}
      <NavLink
        to="/dashboard"
        className="mobile-dashboard-corner"
        aria-label="Dashboard"
      >
        <span className="mobile-dashboard-corner__icon">📊</span>
        Dashboard
      </NavLink>
    </>
  );
}

// Mobile dashboard corner button export for App.js usage (render outside drawer)